import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { useAuth } from "../lib/auth";
import { useWhatsApp, registerGate, setKnownUser, saveDetails, postWhatsAppLead, leadContext, waHref, waMessage, withDetails, guessIntent, validPhone, validEmail } from "../lib/whatsapp";
import { WhatsAppIcon } from "./WhatsAppButton";

const INTENTS = ["Tourist visa", "Business visa", "Student visa", "Work visa", "Documents & checklist", "Appointment or dates", "Pricing", "Something else"];
const field = "h-11 w-full rounded-xl border bg-background px-3 text-sm outline-none focus:border-[#25D366]";

/* One optional step before WhatsApp opens: name, number, e-mail and what the visitor needs, so Ops gets a lead with the page and visa. Never blocks: "continue without details" always works. */
export function WhatsAppGate() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const wa = useWhatsApp();
  const [req, setReq] = useState(null);
  const [f, setF] = useState({ name: "", phone: "", email: "" });
  const [intent, setIntent] = useState("");
  const [err, setErr] = useState({});
  useEffect(() => { setKnownUser(user); return () => setKnownUser(null); }, [user]);
  useEffect(() => registerGate((r) => { setIntent(r.opts.intent || guessIntent()); setErr({}); setReq(r); }), []);
  useEffect(() => { setReq(null); }, [pathname]);
  if (!req) return null;
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); if (err[k]) setErr({ ...err, [k]: null }); };
  const open = (details) => {
    const opts = { ...req.opts, intent };
    const href = waHref(req.number, waMessage(withDetails(opts, details)));
    if (details.phone || details.email) { saveDetails(details); postWhatsAppLead({ ...details, ...leadContext(opts) }); }
    window.open(href, "_blank", "noopener");
    setReq(null);
  };
  const submit = (e) => {
    e.preventDefault();
    const d = { name: f.name.trim(), phone: f.phone.trim(), email: f.email.trim() };
    const bad = { phone: d.phone && !validPhone(d.phone) ? "Check the number (with country code, e.g. +91 98765 43210)" : null, email: d.email && !validEmail(d.email) ? "Check the e-mail address" : null };
    if (bad.phone || bad.email) { setErr(bad); return; }
    open(d.phone || d.email ? d : { name: d.name });
  };
  const hasDetails = !!(f.phone.trim() || f.email.trim());
  return (
    <Dialog open onOpenChange={(o) => !o && setReq(null)}>
      <DialogContent className="max-w-md rounded-card p-6 sm:p-7" data-testid="whatsapp-gate">
        <div className="flex items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#25D366] text-white"><WhatsAppIcon className="h-6 w-6" /></span>
          <div>
            <DialogTitle className="text-lg font-semibold">Chat with the Vistolane team</DialogTitle>
            <DialogDescription className="text-xs text-label2"><span className={wa.online ? "font-medium text-success-strong" : "font-medium text-label1"} data-testid="whatsapp-gate-status">{wa.status}</span> · Mon–Fri 9 AM–6 PM IST</DialogDescription>
          </div>
        </div>
        <form className="mt-4 space-y-3" onSubmit={submit} data-testid="whatsapp-gate-form">
          <div>
            <p className="text-xs font-medium text-label2">What do you need help with?</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="whatsapp-gate-intents">
              {INTENTS.map((i) => <button key={i} type="button" onClick={() => setIntent(i)} className={`h-8 rounded-full px-3 text-xs font-semibold transition-colors ${intent === i ? "bg-foreground text-background" : "bg-gray6 text-label1 hover:bg-gray5"}`} aria-pressed={intent === i} data-testid={`whatsapp-gate-intent-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{i}</button>)}
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <input value={f.name} onChange={set("name")} placeholder="Your name" autoComplete="name" className={`${field} border-separator`} data-testid="whatsapp-gate-name" />
            <input value={f.phone} onChange={set("phone")} placeholder="WhatsApp number" type="tel" autoComplete="tel" inputMode="tel" aria-invalid={!!err.phone} className={`${field} ${err.phone ? "border-danger" : "border-separator"}`} data-testid="whatsapp-gate-phone" />
          </div>
          <input value={f.email} onChange={set("email")} placeholder="E-mail (optional)" type="email" autoComplete="email" aria-invalid={!!err.email} className={`${field} ${err.email ? "border-danger" : "border-separator"}`} data-testid="whatsapp-gate-email" />
          {(err.phone || err.email) && <p className="text-xs text-danger" role="alert" data-testid="whatsapp-gate-error">{err.phone || err.email}</p>}
          <button type="submit" className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#25D366] text-sm font-semibold text-white transition-[transform,filter] hover:brightness-105 active:scale-[0.99]" data-testid="whatsapp-gate-open"><WhatsAppIcon className="h-5 w-5" /> {hasDetails ? "Open WhatsApp" : "Open WhatsApp"}</button>
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-label2">
            <span>Only used to follow up on your question.</span>
            <button type="button" onClick={() => open({})} className="whitespace-nowrap font-medium text-foreground underline-offset-2 hover:underline" data-testid="whatsapp-gate-skip">Continue without details →</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
